import React, { ReactNode } from 'react';
import SaveDot, { SaveState } from '../SaveDot';
import './field.scss';

interface FieldProps {
  label: ReactNode;
  /** Id of the control, when it is a native input; the label then focuses it. */
  htmlFor?: string;
  required?: boolean;
  /** One line under the control. */
  hint?: ReactNode;
  /** Replaces the hint while present. */
  error?: ReactNode;
  /** Right-aligned control in the label row: "+ add contact", a disclosure chevron. */
  action?: ReactNode;
  /** This field's own save dot, beside the label. */
  saveState?: SaveState;
  /** `row` puts the label beside the control, for short settings-style rows. */
  layout?: 'stacked' | 'row';
  className?: string;
  children: ReactNode;
}

/**
 * Label-above field wrapper: label row (label, required mark, save dot,
 * action), the control, then a hint or an error. It does not style the
 * control itself -- that is TextInput, Toggle, Dropdown, and so on.
 */
const Field: React.FC<FieldProps> = ({
  label,
  htmlFor,
  required = false,
  hint,
  error,
  action,
  saveState,
  layout = 'stacked',
  className = '',
  children,
}) => {
  const labelInner = (
    <>
      {label}
      {required && (
        <span className="field__required" aria-hidden="true">
          *
        </span>
      )}
    </>
  );

  return (
    <div className={`field field--${layout} ${error ? 'has-error' : ''} ${className}`.trim()}>
      <div className="field__label-row">
        {htmlFor ? (
          <label className="field__label" htmlFor={htmlFor}>
            {labelInner}
          </label>
        ) : (
          <span className="field__label">{labelInner}</span>
        )}
        {saveState && saveState !== 'idle' && <SaveDot state={saveState} className="field__save" />}
        {action && <span className="field__action">{action}</span>}
      </div>
      <div className="field__control">{children}</div>
      {error ? (
        <p className="field__error" role="alert">
          {error}
        </p>
      ) : (
        hint && <p className="field__hint">{hint}</p>
      )}
    </div>
  );
};

export default Field;
